import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { api, errorMessage } from "../api/client";
import { Button, Dialog, Field, Input, Notice } from "./ui";

/** Server rule (auth/passwords.validate_password): at least 12 characters. */
export const MIN_PASSWORD_LENGTH = 12;

/** Self-service password change (POST /api/auth/password; 204 on success). */
export function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const m = useMutation({
    mutationFn: () => api.post<void>("/api/auth/password", { current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
    },
  });
  const mismatch = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;

  function close() {
    setOpen(false);
    m.reset();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mismatch && !tooShort && current && next) m.mutate();
  }

  return (
    <>
      <button
        type="button"
        className="hidden rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 sm:inline dark:text-zinc-300 dark:hover:bg-zinc-800"
        onClick={() => setOpen(true)}
      >
        Password
      </button>
      <Dialog open={open} onClose={close} title="Change password">
        {m.isSuccess ? (
          <div className="space-y-3">
            <Notice tone="good">Password changed.</Notice>
            <div className="flex justify-end">
              <Button onClick={close}>Close</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Current password">
              {(id) => <Input id={id} type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />}
            </Field>
            <Field label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
              {(id) => <Input id={id} type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} />}
            </Field>
            <Field label="Confirm new password">
              {(id) => <Input id={id} type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
            </Field>
            {tooShort ? <p className="text-xs text-rose-700 dark:text-rose-400">The new password is too short.</p> : null}
            {mismatch ? <p className="text-xs text-rose-700 dark:text-rose-400">The passwords do not match.</p> : null}
            {m.isError ? <Notice tone="bad">{errorMessage(m.error)}</Notice> : null}
            <div className="flex justify-end gap-2">
              <Button onClick={close}>Cancel</Button>
              <Button type="submit" variant="primary" busy={m.isPending} disabled={!current || !next || mismatch || tooShort}>
                Change password
              </Button>
            </div>
          </form>
        )}
      </Dialog>
    </>
  );
}
